import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  WORKFLOW_STAGE_META,
  workflowStageFromLifecycle,
  type WorkflowStage,
} from "@/lib/workflowStages";
import type { ContractLifecycleState } from "@/components/LifecycleStateBadge";

interface WorkflowStageBadgeProps {
  /** Either pass the precomputed stage, or the lifecycleState to derive from. */
  stage?: WorkflowStage;
  lifecycleState?: ContractLifecycleState;
  /**
   * Server-computed signal indicating that internal approvals have
   * cleared (current approval row is executed, or in_progress at the
   * signature/executed stage). When true AND lifecycle is still in a
   * pre-send state, the badge upgrades from "Internal Review" /
   * "Draft" to "Ready" so the contracts list can communicate that the
   * next action is external send.
   */
  approvalCleared?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const TONE_CLASSES: Record<string, string> = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  info:    "bg-blue-50 text-blue-800 border-blue-200",
  primary: "bg-purple-50 text-purple-800 border-purple-200",
  success: "bg-green-50 text-green-800 border-green-200",
};

/**
 * Compact "Draft / In Review / Ready / Negotiation / Signing / Executed"
 * chip used in the contracts list and anywhere the high-level workflow
 * stage needs to be surfaced inline.
 */
export function WorkflowStageBadge({
  stage,
  lifecycleState,
  approvalCleared,
  size = "sm",
  className,
}: WorkflowStageBadgeProps) {
  const PRE_SEND_LIFECYCLES: ContractLifecycleState[] = [
    "draft",
    "internal_review",
    "legal_review",
  ];
  const baseStage: WorkflowStage =
    stage ?? (lifecycleState ? workflowStageFromLifecycle(lifecycleState) : "draft");
  // Upgrade to "Ready" when internal approvals have cleared but the
  // contract is still in a pre-send lifecycle state. Without this the
  // contracts-list badge can't represent the "Ready to Send" stage,
  // since per-row data is lifecycle-only.
  const resolved: WorkflowStage =
    approvalCleared && lifecycleState && PRE_SEND_LIFECYCLES.includes(lifecycleState)
      ? "ready_to_send"
      : baseStage;
  const meta = WORKFLOW_STAGE_META[resolved];
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-medium",
        TONE_CLASSES[meta.tone],
        size === "sm" ? "text-[10.5px] px-1.5 py-0.5" : "text-xs px-2 py-0.5",
        className,
      )}
      data-testid={`workflow-stage-badge-${resolved}`}
      title={`Workflow stage: ${meta.label}`}
    >
      {meta.shortLabel}
    </Badge>
  );
}
